/** @hidden */
import semver from 'semver'
import { BridgeError } from '../config/error'
import { ScopeValidation } from '../config/scope'
import { PermissionType } from '../types/public'
import { generateRequestTransId, verifyMobilePlatform } from '../utils'

// spec https://appsynth.atlassian.net/wiki/spaces/SAPS/pages/2253488161/Javascript+SDK+Spec
export default class Bridge {
  private appId: string
  private version: string
  private timeout: number
  private platform: MobilePlatform
  private callbacks: Map<string, (res: NativeResponse) => void> = new Map()
  private isDebug: boolean = false
  private scopeValidation: ScopeValidation

  constructor(config: BridgeInitParams) {
    const { appId, version, timeout, callbacks, isDebug = false } = config
    this.appId = appId
    this.version = version
    this.timeout = timeout
    this.platform = verifyMobilePlatform()
    this.callbacks = callbacks
    this.isDebug = isDebug
    this.scopeValidation = new ScopeValidation(isDebug)
  }

  async setMiniAppScope(scope: string[]): Promise<void> {
    this.scopeValidation.setScope(scope)
  }

  async getEnvironment(): Promise<GetEnvironmentBridgeResponse> {
    return await this.callHandler<GetEnvironmentBridgeResponse>(
      'getEnvironment'
    )
  }

  async closeWebView(): Promise<void> {
    return await this.callHandler<void>('closeWebView')
  }

  async openMyCoupon(): Promise<void> {
    await this.callHandler<void>('openMyCoupon')
  }

  async getCurrentLocation(): Promise<GPSLocation> {
    return await this.callHandler<GPSLocation>(
      'getCurrentLocation',
      {},
      { ignoreTimeout: true }
    )
  }

  async getUserStatus(): Promise<UserStatus> {
    return await this.callHandler<UserStatus>('getUserStatus')
  }

  async scanCode(): Promise<ScanCodePayload> {
    return await this.callHandler<ScanCodePayload>(
      'scanCode',
      {},
      { ignoreTimeout: true }
    )
  }

  async registerAllMember(): Promise<any> {
    return await this.callHandler<any>(
      'registerAllMember',
      {},
      { ignoreTimeout: true }
    )
  }

  async register7App(): Promise<any> {
    return await this.callHandler<any>(
      'register7App',
      {},
      {
        ignoreTimeout: true,
      }
    )
  }

  async registerCustomer(): Promise<any> {
    return await this.callHandler<any>(
      'registerCustomer',
       {},
      {
        ignoreTimeout: true 
      }
    )
  }

  async acceptTutorial(): Promise<void> {
    return await this.callHandler<void>('Tutorial.accept')
  }

  async cancelTutorial(): Promise<void> {
    return await this.callHandler<void>('Tutorial.cancel')
  }

  async playSound(
    assetId: string,
    options?: PlaySoundOptions
  ): Promise<unknown> {
    const payload = {
      assetId,
      ...options,
    }
    return await this.callHandler<void>('Sound.play', { data: { payload } })
  }

  async stopSound(assetId: string): Promise<unknown> {
    const payload = {
      assetId,
    }
    return await this.callHandler<void>('Sound.stop', { data: { payload } })
  }

  async soundOn(): Promise<void> {
    return await this.callHandler<void>('Sound.on')
  }

  async soundOff(): Promise<void> {
    return await this.callHandler<void>('Sound.off')
  }

  async deleteModel(modelId: string): Promise<void> {
    const payload = {
      modelId,
    }
    return await this.callHandler<void>('ARScene.deleteModel', {
      data: { payload },
    })
  }

  async startAR(payload: ARSceneConfiguration): Promise<void> {
    return await this.callHandler<void>(
      'ARScene.start',
      { data: { payload } },
      { ignoreTimeout: true }
    )
  }

  async endAR(): Promise<void> {
    return await this.callHandler<void>('ARScene.end')
  }

  async preloadAssets(payload: AssetPayload): Promise<void> {
    return await this.callHandler<void>(
      'Asset.preload',
      { data: { payload } },
      { ignoreTimeout: true }
    )
  }

  async checkPreloadAssetsStatus(): Promise<PreloadAssetsStatus> {
    return await this.callHandler<PreloadAssetsStatus>(
      'Asset.checkPreloadStatus'
    )
  }

  async showOverlay(payload: ShowOverlayPayload): Promise<void> {
    return await this.callHandler<void>('ARScene.showOverlay', {
      data: { payload },
    })
  }

  async checkCompatibility(): Promise<void> {
    const sdkVersion = semver.valid(semver.coerce(this.version))
    const payload = { sdkVersion }
    return await this.callHandler<void>('checkCompatibility', {
      data: { payload },
    })
  }

  async isPermissionAllowed(permissionType: PermissionType): Promise<boolean> {
    const payload = {
      permissionType,
    }
    return await this.callHandler<boolean>('isPermissionAllowed', {
      data: { payload },
    })
  }
  async requestPermission(permissionType: PermissionType): Promise<undefined> {
    const payload = {
      permissionType,
    }
    return await this.callHandler<undefined>('requestPermission', {
      data: { payload },
    })
  }

  async trackAnalytic(eventName: string, params: any): Promise<void> {
    // docs https://appsynth.atlassian.net/wiki/spaces/SAPS/pages/2253488161/MiniApp+Javascript+SDK+and+JS+Bridge+Specification#Analytic-Tracking-Functions
    const payload: any = { event: eventName, param: { appId: this.appId } }
    // NOTE all value in params must be ONLY string
    if (params && Object.entries(params).length) {
      payload.param = {
        ...payload.param,
        ...params,
      }
    }

    return await this.callHandler<void>('trackAnalytic', {
      data: {
        payload,
      },
    })
  }

  async exchangeToken() {
    return await this.callHandler<ExchangeTokenPayload>('exchangeToken', {
      data: { payload: { appId: this.appId } },
    })
  }

  async getUserId(): Promise<string> {
    return await this.callHandler<string>('getUserId')
  }

  async getUserProfileImage(): Promise<UserProfileImage> {
    return await this.callHandler<UserProfileImage>('getUserProfileImage')
  }

  async reloadWebView(): Promise<void> {
    return await this.callHandler<void>('reloadWebView')
  }

  async updateARSceneContent(payload: ARSceneContent): Promise<void> {
    return await this.callHandler<void>('ARScene.updateContent', {
      data: {
        payload,
      },
    })
  }

  async navigateByNavPage(
    navPage: String,
    subNavPage: String | null,
    additionalData: any
  ): Promise<void> {
    const payload = {
      navPage,
      subNavPage,
      additionalData,
    }
    return await this.callHandler<void>('navigateByNavPage', {
      data: {
        payload,
      },
    })
  }

  async getLatestLoginProvider(): Promise<GetLatestLoginProviderResponse> {
    return await this.callHandler<GetLatestLoginProviderResponse>(
      'getLatestLoginProvider'
    )
  }

  async openPDPA(payload: OpenPDPAPayload): Promise<OpenPDPAResponse> {
    return await this.callHandler<OpenPDPAResponse>(
      'openPDPA',
      {
        data: { payload },
      },
      { ignoreTimeout: true }
    )
  }

  // core handler to communicate with native code
  private async callHandler<T>(
    nativeFunctionName: MobileNativeFunctions,
    params: CallHandlerPayload = {},
    options: CallHandlerOption = {
      ignoreTimeout: false,
    }
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      // validate scope
      const validScope = this.scopeValidation.validateScope(nativeFunctionName)
      if (!validScope) {
        reject(
          new BridgeError('INVALID_SCOPE', {
            cause: `Invalid scope from calling function ${nativeFunctionName}`,
          })
        )
        return
      }

      // TODO verify payload
      const transId = generateRequestTransId()
      const callbackName = `fn${transId}` // ex. fn129slkdfj923

      // compose native call message
      const jsonMessage = {
        ...params,
        appId: this.appId,
        requestTrans: transId,
        functionName: nativeFunctionName,
        data: {
          ...params.data,
          mode: 'callbackFunction',
          callbackSuccess: `MiniAppSDK.callbacks.get('${callbackName}')`,
          callbackFail: `MiniAppSDK.callbacks.get('${callbackName}')`,
          payload: params.data?.payload || {},
        },
      }

      const message = JSON.stringify(jsonMessage)

      if (this.isDebug) {
        console.log(`[DEBUG] ✉️  sent bridge message ${nativeFunctionName}`, {
          message: jsonMessage,
        })
      }

      let timeout: NodeJS.Timeout
      if (!options.ignoreTimeout) {
        timeout = setTimeout(() => {
          const func = this.callbacks.get(callbackName)
          if (func) {
            reject(
              new BridgeError('TIMEOUT', {
                cause: `from ${nativeFunctionName} function`,
              })
            )
          }
        }, this.timeout)
      }

      // create a callback function listen to mobile success/fail signal if done successfully or not
      // aka mobileDone from the old SDK

      this.callbacks.set(callbackName, (mobileResponse: string) => {
        let jsonResp = null
        if (!options.ignoreTimeout) {
          clearTimeout(timeout)
        }
        try {
          jsonResp = JSON.parse(mobileResponse)
          if (this.isDebug) {
            console.log(`[DEBUG] ✅ callback ${nativeFunctionName} success`, {
              response: jsonResp,
            })
          }
        } catch {
          reject(
            new BridgeError('CANNOT_PASRE_MOBILE_RESPONSE', {
              cause: `from ${nativeFunctionName} original resp ${mobileResponse}`,
            })
          )
        }

        if ((jsonResp as MobileErrorResponseType).error) {
          reject(
            new BridgeError('MOBILE_FORWARDED_ERROR', {
              errorCode: jsonResp.error.errorCode,
              errorMessage: jsonResp.error.errorMessage,
            })
          )
        }

        if ((jsonResp as MobileSuccessResponseType).data) {
          resolve(jsonResp.data)
        } else {
          reject(
            new BridgeError('NOT_FOUND_KEY_DATA', {
              cause: `called from ${nativeFunctionName} original resp ${mobileResponse}`,
            })
          )
        }
        // Remove the callback from cache
        this.callbacks.delete(callbackName)
      })

      if (this.platform === 'iOS') {
        window.webkit.messageHandlers.webEchoMessage.postMessage(message)
      } else if (this.platform === 'Android') {
        Android.webEchoMessage(message)
      }
    })
  }
}
